import { RequestStatus, SortOrder } from 'typed/api';
import { Action, ActionType } from 'typed/actions';
import { IReducer, networkActionReducer } from './';
import { Entity, IEntityReducerState, NetworkArray, NetworkPaginationSlice, isNetworkArray, isPaginationSlice, isSimpleArray } from './entityReducers';
import { deepAssign, deepClone } from 'typed/utils';
import { SystemEnumerationEntity } from 'typed/api/entities';

type PID<S> =
    S extends NetworkPaginationSlice<infer e> ? e['ID'] :
    S extends NetworkArray<infer e> ? e['ID'] :
    S extends Entity[] ? Entity['ID'] :
    never;

export default function entityLoadAllReducer<E extends SystemEnumerationEntity<any>, S extends NetworkPaginationSlice<E>, T extends ActionType>(def: S, type: T, ensureSyncWith: { [k: string]: PID<S>; }, entityName: string): IReducer<S, Action<T>>;
export default function entityLoadAllReducer<E extends SystemEnumerationEntity<any>, S extends NetworkArray<E>, T extends ActionType>(def: S, type: T, ensureSyncWith: { [k: string]: PID<S>; }, entityName: string): IReducer<S, Action<T>>;
export default function entityLoadAllReducer<E extends SystemEnumerationEntity<any>, S extends E[], T extends ActionType>(def: S, type: T, ensureSyncWith: { [k: string]: PID<S>; }, entityName: string): IReducer<S, Action<T>>;
export default function entityLoadAllReducer<E extends Entity, S extends NetworkPaginationSlice<E>, T extends ActionType>(def: S, type: T): IReducer<S, Action<T>>;
export default function entityLoadAllReducer<E extends Entity, S extends NetworkArray<E>, T extends ActionType>(def: S, type: T): IReducer<S, Action<T>>;
export default function entityLoadAllReducer<E extends Entity, S extends E[], T extends ActionType>(def: S, type: T): IReducer<S, Action<T>>;
export default function entityLoadAllReducer<E extends Entity, T extends ActionType>(def: IEntityReducerState<E>, type: T, ensureSyncWith?: { [k: string]: E['ID']; }, entityName?: string): IReducer<any, Action<T>> {
    const nr = networkActionReducer<T, any>(type);
    return function entityLoadAllReducer(state: IEntityReducerState<E> | undefined = deepClone(def), action: Action<T>): IEntityReducerState<E> {
        if (action.type === type) {
            if (isPaginationSlice(state) && action.payload.status === RequestStatus.PENDING && !action.payload.startID) {
                state = def;
            }

            if (action.payload.status === RequestStatus.SUCCEEDED) {
                if (ensureSyncWith) {
                    const response = action.payload.response as Array<SystemEnumerationEntity<string, number>>;
                    for (let i = 0; i < response.length; i++) {
                        if (ensureSyncWith[response[i].Identifier.replace(/([a-z])([A-Z]+)/g, '$1_$2').toUpperCase()] !== response[i].ID) {
                            // eslint-disable-next-line no-console
                            if (DEBUG) { console.error(`UI and ${entityName} are out of sync`); }
                            break;
                        }
                    }
                    for (const i in ensureSyncWith) {
                        if (typeof ensureSyncWith[i] === 'number' && !response.getByID(ensureSyncWith[i])) {
                            // eslint-disable-next-line no-console
                            if (DEBUG) { console.error(`UI and ${entityName} are out of sync`); }
                            break;
                        }
                    }
                }
            }

            const
                oldData = isNetworkArray(state) ? state.Data : isSimpleArray(state) ? state : [state],
                newData = action.payload.status === RequestStatus.SUCCEEDED ?
                    deepAssign(oldData,
                        isPaginationSlice(state) ?
                            action.payload.startID ?
                                action.payload.response.Order === SortOrder.DESCENDING ?
                                    [...action.payload.response.Data, ...oldData] :
                                    [...oldData, ...action.payload.response.Data] :
                                action.payload.response.Data :
                            action.payload.response
                    ) :
                    oldData;

            return isPaginationSlice(state) && action.payload.status === RequestStatus.SUCCEEDED ?
                nr(deepAssign(state, {
                    ...action.payload.response,
                    Data: newData,
                    Count: newData.length,
                    Custom: state.Custom
                }), action) :
                isNetworkArray(state) ?
                    nr(deepAssign(state, { Data: newData }), action) :
                    isSimpleArray(state) ?
                        newData :
                        newData[0];
        }

        return state;
    };
}
